import {SignInUrlBuilder} from '@tableau/shared-urls';
import { ToastWidget } from '@tableau/widgets';
import _ from 'underscore';
import * as URI from 'uri-js';

import {SessionService} from '~commonInfra/services/SessionService';
import {removeQueryParamFromString, WindowLocationService} from '~commonInfra/services/WindowLocationService';
import {getSiteUrlNameIfOnSigninPath, isVizUrl} from '~commonInfra/url/UrlHelpers';
import {UrlParamKeys} from '~commonInfra/url/UrlParamKeys';
import {CommonErrorCode, UninitializedServerErrorCode} from '~commonInfra/utils/ErrorCodes';
import {Helpers} from '~commonInfra/utils/Helpers';
import {LoginUrlsWrapper} from '~src/authentication/components/loginSpace/LoginUrls';
import {AuthenticationService} from '~src/authentication/services/AuthenticationService';
import {AuthenticationTypeResolutionService} from '~src/authentication/services/AuthenticationTypeResolutionService';
import {UnauthenticatedUserHandler} from '~src/authentication/services/UnauthenticatedUserHandler';
import {getAppStore} from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import {RootAdminUiState} from '~src/rootAdminUi/RootAdminUIState';
import {setRootAdminUiState} from '~src/rootAdminUi/RootAdminUiStateActions';
import {getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';
import {ServerSettingsUnauthenticated} from '~src/settings/services/ServerSettingsUnauthenticated';
import { PopupToasterStyle } from '~src/spacesPlatform/components/layout/ToasterStyles';
import {VizService} from '~src/viz/services/VizService';

export function startSessionPing(vizService: VizService) {

const sessionManagementService = getSessionManagementServiceInstance();

function redirectToLogin(errorCode: number) {
  // Remove auth type from the current URL before building the redirect URL.
  const authenticationOverride = (WindowLocationService.getHashQueryObject() as any)[UrlParamKeys.AuthenticationTypeOverride];

  const params: SignInUrlBuilder.QueryParams = {
    redirect: removeQueryParamFromString(window.location.hash, UrlParamKeys.AuthenticationTypeOverride).substr(1) || null,
    error: errorCode === CommonErrorCode.InvalidSession ? null : errorCode
  };

  if (AuthenticationTypeResolutionService.isAuthenticationAutoLogin()) {
    // The signin state will attempt to auto-login by default. We don't want it to here.
    params.disableAutoSignin = true;
  }
  const siteUrlName = getSiteUrlNameIfOnSigninPath(WindowLocationService.getFragmentWithoutQuery(), WindowLocationService.getHashQueryObject());
  if (siteUrlName) {
    params.site = siteUrlName;
  }

  // Pass over the auth type override as a param for the login page.
  if (authenticationOverride) {
    params.auth = authenticationOverride;
  }
  LoginUrlsWrapper.navigateToSignIn(params);
}

function shouldRedirectToLogin(nextUrl: string) {
  const isNextStateAnonymous = UnauthenticatedUserHandler.isAnonymousUrl(nextUrl);
  const isNonVizPageWithRestrictedSession =
    sessionManagementService.isSessionRestrictedTrustedTicket() && nextUrl && !isVizUrl(nextUrl);
  return !isNextStateAnonymous && (!sessionManagementService.inSession() || isNonVizPageWithRestrictedSession);
}

function shouldAutoLogin(nextUrl: string) {
  const isNextStateAnonymous = UnauthenticatedUserHandler.isAnonymousUrl(nextUrl);
  return !sessionManagementService.inSession() && !isNextStateAnonymous && AuthenticationTypeResolutionService.isAuthenticationAutoLogin();
}

function shouldRedirectToEmbeddedViz(nextUrl: string) {
  const isPhone = Math.min(screen.width, screen.height) <= ServerSettingsUnauthenticated.dsdPhoneSize();

  if (nextUrl && isVizUrl(nextUrl)) {
    return vizService.isEmbedParamOn() || sessionManagementService.isSessionRestrictedTrustedTicket() || isPhone;
  }

  return false;
}

function shouldRedirectToGuestViz(nextUrl: string) {
  return nextUrl && isVizUrl(nextUrl) && !sessionManagementService.inSession() && ServerSettingsUnauthenticated.isGuestEnabled();
}

function shouldRedirectToVizExport(nextUrl: string) {
  return nextUrl && isVizUrl(nextUrl) && vizService.pathHasExportableFileType();
}

function maybeVizRedirect(nextUrl: string, shouldRedirectWithoutAddingToHistory: boolean) {
  if (shouldRedirectToVizExport(nextUrl)) {
    WindowLocationService.navigateToWithoutAddingToHistory(vizService.getVizqlUrl());
    return true;
  } else if (shouldRedirectToEmbeddedViz(nextUrl)) {
    vizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory);
    return true;
  } else if (shouldRedirectToGuestViz(nextUrl)) {
    vizService.redirectToEmbeddedMode(shouldRedirectWithoutAddingToHistory, {isFromGuest: true});
    return true;
  }
  return false;
}

function shouldRedirectToSitePicker(props: {isSuccessfulSamlLogin: boolean}) {
  // This will be used my OnPrem & Online SAML flows.
  return props.isSuccessfulSamlLogin && isRootUrl();
}

function isRootUrl() {
  return WindowLocationService.getFragmentWithoutQuery() === '' ||
         WindowLocationService.getFragmentWithoutQuery() === '#' ||
         WindowLocationService.getFragmentWithoutQuery() === '#/';
}

// VizPortal 9.0 Session Expiration Solution - B144748
// 1) Only the getActionInfo and getSessionInfo requests will extend your session
// 2) As long as the user is “interacting ”, getSessionInfo will be requested once every 5 minutes or half
//    the configured idle time limit (wgserver.session.idle_limit), whichever is less
// 3) We determine if the user is “interacting” by listening to mousemove and keydown events in the browser
function initializeSessionPing() {
  // If the browser tab comes into focus we want to refresh the session info.
  // Do this at most once every 15 seconds
  window.addEventListener('focus', _.throttle((_event: any) => {
    refreshSession();
  }, 15000, {trailing: false}));

  const fiveMinutesInMilliseconds = 5 * 60 * 1000;
  const halfSessionIdleLimitInMilliseconds = sessionManagementService.getSessionIdleLimitSeconds() * 1000;
  let sessionPingPeriodWhileUserIsInteracting = Math.min(fiveMinutesInMilliseconds, halfSessionIdleLimitInMilliseconds);

  // Don't let the period fall below 15 seconds, because that's absurdly short
  sessionPingPeriodWhileUserIsInteracting = Math.max(sessionPingPeriodWhileUserIsInteracting, 15 * 1000);

  const listenForInteractionAfterSessionPingPeriod = () => {
    // timeout for session ping period, then listen to interaction events
    window.setTimeout(() => {
      // $.one will bind to the event once per element per event type. In this case the handler will
      // be fired twice if mousemove and keydown both fire, so ensure the handler is only called once
      const handler = _.once((_event: Event) => {
        // User is alive! ping the sessionManagementService, then sleep on it
        refreshSession();
        listenForInteractionAfterSessionPingPeriod();
      });
      ['keydown', 'mousemove', 'touchstart'].forEach(type => window.addEventListener(type, handler, {once: true}));
    }, sessionPingPeriodWhileUserIsInteracting);
  };

  // Kick it off
  listenForInteractionAfterSessionPingPeriod();
}

function refreshSession() {
  if (FeatureFlags.refreshSessionEnabled) {
    // call the lightweight refreshSession API that only refreshes session
    sessionManagementService.refreshSession();
  } else {
    // call getSessionInfo API that refreshes session, but also pulls site settings, server settings, FFs etc.
    // This will go away once we enable feature and remove FF
    sessionManagementService.refreshSessionInfo(true);
  }
}

  // The client is hidden initially. We should show it once we know we will be displaying UI
  // (as opposed to navigating away from vizportal client).
  function showClient(rootAdminUiState: RootAdminUiState) {
    getAppStore().dispatch(setRootAdminUiState(rootAdminUiState));
    document.body.style.display = 'block';
  }

function bootstrapCheckInitialAdmin(code: number, nextUrl: string) {
  // Defect 458853:[LC] Intermittent Calls to getSessionInfo clears workgroup_session_id, which makes data source authentication imposible
  // When user not authenticated on Tableau Server, data source authentication dialog
  // will get temporary guest session in order to client to encrypt credentials and sessionManagementService to descrypt them
  // But calls to getSessionInfo will clear session cookie. Disabling calls to getSessionInfo when requested.
  const fragment = URI.parse(nextUrl).fragment;
  if (typeof fragment === 'undefined' ||
    (!fragment.startsWith('/authenticate') &&
      !fragment.startsWith('/pkce') &&
      !fragment.startsWith('/signin'))) {
    // Initializing sesssion ping only when LazyConnections disabled or disableSessionPing was NOT explicitly requested in state params
    initializeSessionPing();
  }

  // overrideAuthenticationTypeBasedOnUrl();
  // Need to know if the user already authenticated or not
  if (code === UninitializedServerErrorCode.UntrustedMachine) {
    showClient('untrusted');
  } else if (code === UninitializedServerErrorCode.ReadyToInitialize) {
    showClient('init');
  } else {
    bootstrapCheckAutoLogin(code, nextUrl);
  }

}

function bootstrapCheckAutoLogin(code: number, nextUrl: string) {
  if (shouldAutoLogin(nextUrl)) {
    // Go to interstitial page that may be showing 'Signing In' spinner
    _.defer(() => showClient('interstitial'));
    AuthenticationService.autoLogin().then(() => {
      getAppStore().dispatch(setRootAdminUiState('default'));
      if (isRootUrl()) {
        navigateToSitePicker();
      } else {
        bootstrapCheckRedirects(code, nextUrl);
      }
    }, (errorCode: number) => {
      getAppStore().dispatch(setRootAdminUiState('default'));
      bootstrapCheckRedirects(errorCode, nextUrl);
    });
  } else {
    bootstrapCheckRedirects(code,  nextUrl);
  }
}

function bootstrapCheckRedirects(code: number, nextUrl: string) {
  if (maybeVizRedirect(nextUrl, true)) {
    // maybeVizRedirect handles the redirect
  } else if (shouldRedirectToLogin(nextUrl)) {
    showClient('default');
    redirectToLogin(code);
  } else if (shouldRedirectToSitePicker({
    isSuccessfulSamlLogin: Helpers.BooleanQueryParamRegex.test((WindowLocationService.getHashQueryObject() as any)[UrlParamKeys.IsFromSaml])
  })) {
    navigateToSitePicker();
  } else {
    // Retry $locationChange
    showClient('default');
  }
}

function navigateToSitePicker() {
  // The user just logged in, and is in the root URL.
  AuthenticationService.doesTheCurrentUserBelongToMoreThanOneSite().then((belongsToMoreThanOneSite: boolean) => {
    if (belongsToMoreThanOneSite) {
      LoginUrlsWrapper.navigateToLoginSitePicker({});
    } else {
      showClient('default');
    }
  }).catch(() => {
    // We failed to check if they belong to multiple sites. We'll send them to site-picker, worst case they only see one value there.
    LoginUrlsWrapper.navigateToLoginSitePicker({});
  });
}

function editingDeviceSpecificAlert() {
  return UrlParamKeys.DeviceType in WindowLocationService.getHashQueryObject() && UrlParamKeys.EditDataAlert in WindowLocationService.getHashQueryObject();
}

function urlChangeListener(newUrl: string, oldUrl: string) {
  if (SessionService.isSessionStateUnknown()) {
    // We've just loaded the app and session state is unknown. We can't decide what
    // to do until we know the session state, so prevent the location change. This
    // will prevent the app from entering the ui router state machine.
    sessionManagementService.bootstrapSession().then((code: number) => {
      bootstrapCheckInitialAdmin(code, newUrl);
      ToastWidget.initToaster(PopupToasterStyle);
    });
  } else if(maybeVizRedirect(newUrl, vizService.shouldRedirectWithoutAddingToHistory(oldUrl)) && (!editingDeviceSpecificAlert())) {
  } else if (shouldRedirectToLogin(newUrl)) {
    redirectToLogin(null);
  }
}

  window.addEventListener('load', (event) => {
    urlChangeListener(window.location.href, window.location.href);
  });

  window.addEventListener('hashchange', (event) => {
    urlChangeListener(event.newURL, event.oldURL);
  });
}